/**
 * =========================================================================================
 * Component - UTable Column Visibility
 * =========================================================================================
 * Self-contained component for managing table column visibility.
 * Renders the + trigger button and the right-side sidebar modal with drag-drop column selection.
 *
 * To hide the button: simply don't pass allowReorderAddColumn to UTable.Table.
 **/

import React, {useEffect, useMemo, useRef, useState} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import PropTypes from 'prop-types';
import * as Icon from 'ubs-icons';
import AddColumn from './add-column';
import SideModal from '../../../side-modal';
import { isMobileDevice, isInApp, showToastSuccess } from '../../function';
import { useUFramework, useDispatchUFramework } from '../../../context';
import UButton from "../../../UButton";
import UPopup from "ubs-framework/v2/UPopup";
import { COLOR } from '../../constant';

const ColumnVisibility = (props) => {
    const {
        columnList,
        savedOrder,
        onSave,
        onReset,
        title,
        tabKey,
        allowedKeys,
        maxSelect,
        exportMode,
        exportEvent,
        exportData,
        trigger,
        open: controlledOpen,
        onClose
    } = props;

    const { sendData, useSocket, ID, SOCKET_ID, table } = useUFramework(e => e);
    const dispatch = useDispatchUFramework();
    const userChangesRef = useRef({});

    // Controlled mode: when `open` is passed, the parent owns visibility (e.g. opening from a custom
    // download menu) and no internal trigger is rendered. Otherwise the trigger toggles internal state.
    const isControlled = controlledOpen !== undefined;
    const [internalOpen, setInternalOpen] = useState(false);
    const open = isControlled ? controlledOpen : internalOpen;
    const [localLoading, setLocalLoading] = useState(false);
    const [reorderedColumns, setReorderedColumns] = useState(null);

    // Export mode persists under its OWN tabKey (separate from the table), so both its saved selection
    // and its allowed columns come from this tabKey's `hrms|master|option|fetch` response (stored in
    // context) rather than the savedOrder/allowedKeys props (which are table-driven).
    // Memoized so the references are stable across renders (otherwise the sync effect below loops forever).
    const _exportSavedOrder = exportMode ? table?.columnPreferences?.[tabKey]?.savedOrder : null;
    const _exportAllowedKeys = exportMode ? table?.columnPreferences?.[tabKey]?.allowedKeys : null;
    
    const effectiveSavedOrder = useMemo(
        () => (exportMode ? (_exportSavedOrder || []) : savedOrder),
        [exportMode, _exportSavedOrder, savedOrder]
    );
    
    const effectiveAllowedKeys = useMemo(
        () => (exportMode ? (_exportAllowedKeys || []) : allowedKeys),
        [exportMode, _exportAllowedKeys, allowedKeys]
    );

    // Build form default values from saved active column keys
    let defaultValues = {};
    effectiveSavedOrder?.forEach(item => { defaultValues[item] = true; });

    const methods = useForm({defaultValues});
    const {handleSubmit} = methods;

    const { off, refresh } = useSocket(res => {
        if (res && !res?.error && !res?.data?.error) {
            switch (res.event) {
                case 'hrms|master|option|fetch':
                    if (res?.data?.name && res?.data?.name !== tabKey) return true;
                    // Only export mode self-fetches its saved selection; the table owns its own fetch.
                    if (!exportMode) return true;
                    dispatch({
                        type: 'SET_COLUMN_PREFERENCE',
                        payload: {
                            key: tabKey,
                            preference: { savedOrder: res?.data?.values || [], allowedKeys: res?.data?.allowed || [] }
                        }
                    });
                    break;
                case 'hrms|master|option|update':
                    if (res?.data?.name && res?.data?.name !== tabKey) return true;

                    dispatch({
                        type: 'SET_COLUMN_PREFERENCE',
                        payload: {
                            key: tabKey,
                            preference: { savedOrder: res?.data?.values || [] }
                        }
                    });
                    // In export mode the download response drives the toast/close; here we just persist.
                    if (!exportMode) {
                        showToastSuccess("List columns updated successfully!");
                        setLocalLoading(false);
                        closeModal();
                    }                    
                    if(exportMode && exportData)  sendData(exportEvent, { ...(exportData || {}) });
                    break;
                case exportEvent: {
                    if (res?.data?.result) return;
                    setLocalLoading(false);
                    if (res?.error || res?.data?.error) return;
                    if (res?.data?.socketId === SOCKET_ID && res?.data?.url) {
                        window.open(res?.data?.url, isInApp() ? '_self' : '_blank');
                    }
                    showToastSuccess(res?.data?.message || "File exporting started, you will shortly receive an email with exported file.");
                    closeModal();
                    break
                }
                default:
                    return true;
            }
        }
    });

    useEffect(() => {
        refresh();
        return () => off();
    }, [tabKey, exportData, exportEvent]);

    // Export mode self-fetches its saved selection (under its own tabKey) the first time the modal opens.
    useEffect(() => {
        if (exportMode && open && tabKey && sendData && ID && !table?.columnPreferences?.[tabKey]) {
            sendData('hrms|master|option|fetch', { user: ID, name: tabKey });
        }
    }, [exportMode, open, tabKey]);

    // Sync form defaults when the saved selection (or the available column count) changes.
    // NOTE: deps are stable references/primitives only — never the raw columnList array, which is
    // recreated every render and would loop this effect (it calls methods.reset → re-render).
    useEffect(() => {
        // Resolve which columns should be checked...
        const selected = new Set();
        if (effectiveSavedOrder && Array.isArray(effectiveSavedOrder) && effectiveSavedOrder.length > 0) {
            effectiveSavedOrder.forEach(item => selected.add(item));
        } else if (effectiveAllowedKeys && effectiveAllowedKeys.length > 0) {
            // No saved selection yet: export mode defaults to all allowed columns (the table leaves them off).
            if (exportMode) effectiveAllowedKeys.forEach(item => selected.add(item));
        } else {
            columnList?.forEach(col => selected.add(col.value));
        }
        // ...then set an EXPLICIT boolean for every column, so reset() unchecks the unselected ones
        // (omitting them would leave previously-checked columns checked).
        const dv = {};
        columnList?.forEach(col => { dv[col.value] = !!col.isMandatory || selected.has(col.value); });
        methods.reset(dv);
    }, [effectiveSavedOrder, effectiveAllowedKeys, columnList?.length, exportMode]);

    const openModal = () => {
        userChangesRef.current = {};
        if (!isControlled) setInternalOpen(true);
    };

    const closeModal = () => {
        setReorderedColumns(null);
        userChangesRef.current = {};
        if (isControlled) onClose?.();
        else setInternalOpen(false);
    };

    // Start fresh each time the modal opens (covers the controlled case where the parent toggles `open`).
    useEffect(() => {
        if (open) userChangesRef.current = {};
    }, [open]);

    const handleFieldChange = (name, checked) => {
        userChangesRef.current[name] = checked;
    };

    const computeSelectedKeys = (data) => {
        const sourceList = reorderedColumns && reorderedColumns.length > 0
            ? reorderedColumns
            : columnList;
        const userChanges = userChangesRef.current || {};
        const selectedKeys = [];
        sourceList.forEach(col => {
            if (col.isMandatory) {
                selectedKeys.push(col.value);
                return;
            }
            let isSelected;
            if (Object.prototype.hasOwnProperty.call(userChanges, col.value)) {
                isSelected = userChanges[col.value] === true;
            } else if (data[col.value] === true) {
                isSelected = true;
            } else if (data[col.value] === undefined) {
                isSelected = !!effectiveSavedOrder?.includes(col.value);
            } else {
                isSelected = false;
            }
            if (isSelected) selectedKeys.push(col.value);
        });
        return selectedKeys;
    };

    const handleSave = (data) => {
        const selectedKeys = computeSelectedKeys(data);
        if (sendData && tabKey) {
            setLocalLoading(true);
            sendData('hrms|master|option|update', {
                user: ID,
                name: tabKey,
                values: selectedKeys
            });
        }
        if (onSave) onSave(selectedKeys, reorderedColumns);
    };

    // Export-mode primary action — persists the selection under its OWN tabKey (so it's remembered,
    // independent of the table) and triggers the export with the selected columns.
    const handleExport = (data) => {
        const selectedKeys = computeSelectedKeys(data);
        setLocalLoading(true);
        // Persist only when something actually changed from what's saved — either the visible-column
        // selection, OR the column order (a drag-reorder). Re-exporting an unchanged view skips the update.
        const saved = effectiveSavedOrder || [];
        const savedSet = new Set(saved);
        const selectionChanged = selectedKeys.length !== saved.length || selectedKeys.some(k => !savedSet.has(k));
        const orderChanged = Array.isArray(reorderedColumns) && reorderedColumns.length > 0;
        const changed = selectionChanged || orderChanged;
        if (sendData && tabKey && changed) {
            sendData('hrms|master|option|update', { user: ID, name: tabKey, values: selectedKeys });
        }
        if (sendData && exportEvent && !changed) {
            sendData(exportEvent, { ...(exportData || {}) });
        }
    };

    const handleReset = () => {
        if (sendData && ID) {
            setLocalLoading(true);
            
            const mandatoryKeys = columnList.filter(col => col.isMandatory).map(col => col.value);
            const listItem = {
                user: ID,
                values: mandatoryKeys,
                name: tabKey
            };
            sendData('hrms|master|option|update', listItem);
        }
        if (onReset) onReset();
    };

    return (
        <React.Fragment>
            {/* Trigger — skipped in controlled mode (parent owns opening); otherwise a custom node or the default + icon */}
            {isControlled ? null : trigger
                ? <span className="u_cp" onClick={openModal}>{trigger}</span>
                : <UPopup
                    params={{className: 'u_tooltip', disabled: isMobileDevice()}}
                    desc={<span>{title}</span>}
                >
                    <span className="u_filter-icon border" onClick={openModal}>
                        <Icon.Add size={20} fill={COLOR.u_icon_bg}/>
                    </span>
                </UPopup>}

            {/* Sidebar Modal */}
            {open && (
                <SideModal
                    open={open}
                    className={'u_sidebar-modal'}
                    maxWidth={340}
                    onClose={closeModal}
                    header={<h4 className='u_fw-700'>{title}</h4>}
                    position="right"
                    footer={
                        exportMode ? (
                            <div className="u_row u_pl-20 u_pr-20">
                                <div className="u_col-6">
                                    <UButton
                                        loading={localLoading}
                                        disabled={localLoading}
                                        variant="white"
                                        type="cancel"
                                        onClick={closeModal}
                                    />
                                </div>
                                <div className="u_col-6">
                                    <UButton
                                        loading={localLoading}
                                        disabled={localLoading}
                                        variant="blue"
                                        type="Export"
                                        cn="u_ml-10"
                                        onClick={handleSubmit(handleExport)}
                                    />
                                </div>
                            </div>
                        ) : (
                            <div className="u_row u_pl-20 u_pr-20">
                                <div className="u_col-6">
                                    <UButton
                                        loading={localLoading}
                                        disabled={localLoading}
                                        variant="white"
                                        type="reset"
                                        onClick={handleSubmit(handleReset)}
                                    />
                                </div>
                                <div className="u_col-6">
                                    <UButton
                                        loading={localLoading}
                                        disabled={localLoading}
                                        variant="blue"
                                        type="save"
                                        cn="u_ml-10"
                                        onClick={handleSubmit(handleSave)}
                                    />
                                </div>
                            </div>
                        )
                    }
                >
                    
                    <FormProvider {...methods}>
                        <AddColumn
                            column={
                                (effectiveAllowedKeys && Array.isArray(effectiveAllowedKeys) && effectiveAllowedKeys.length > 0)
                                    ? (reorderedColumns || columnList)?.filter(col => effectiveAllowedKeys.includes(col.value) && col.visible !== false)
                                    : (reorderedColumns || columnList)?.filter(col => col.visible !== false)
                            }
                            tabKey={tabKey}
                            onColumnReorder={setReorderedColumns}
                            savedOrder={effectiveSavedOrder}
                            maxSelect={maxSelect}
                            onFieldChange={handleFieldChange}
                        />
                    </FormProvider>
                </SideModal>
            )}
        </React.Fragment>
    );
};

ColumnVisibility.propTypes = {
    columnList: PropTypes.array.isRequired,
    savedOrder: PropTypes.array,
    onSave: PropTypes.func,
    onReset: PropTypes.func,
    title: PropTypes.string,
    tabKey: PropTypes.string,
    maxSelect: PropTypes.number,
    // Export mode: opens via `trigger`, never persists, primary button exports selected columns
    exportMode: PropTypes.bool,
    exportEvent: PropTypes.string,
    exportData: PropTypes.object,
    trigger: PropTypes.node,
    // Controlled visibility — pass `open`/`onClose` to drive the modal externally (e.g. from a download menu)
    open: PropTypes.bool,
    onClose: PropTypes.func,
};

ColumnVisibility.defaultProps = {
    savedOrder: [],
    title: 'Add Column',
    maxSelect: 0,
    exportMode: false,
};

export default ColumnVisibility;
